import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '../ui/modal';
import { CheckIcon, FlagIcon } from '../ui/icons';
import { Button } from '../ui/button';
import { CreateReportRequest } from '../../types/Report.api.type';

/* 신고 사유: 추후 DB로 이동 */
const REPORT_REASONS = [
  { id: 'SPAM', label: '스팸 또는 광고', description: '상업적 광고, 홍보성 내용' },
  { id: 'INAPPROPRIATE', label: '부적절한 내용', description: '욕설, 비방, 혐오 표현' },
  { id: 'HARASSMENT', label: '괴롭힘 또는 폭력', description: '개인 공격, 위협적 내용' },
  { id: 'MISINFORMATION', label: '허위정보', description: '거짓 정보 유포' },
  { id: 'COPYRIGHT', label: '저작권 침해', description: '무단 도용, 복제' },
  { id: 'PRIVACY', label: '개인정보 노출', description: '개인정보 무단 공개' },
  { id: 'OTHER', label: '기타', description: '위에 해당하지 않는 사유' },
] as const;

interface ReportModalProps {
  isOpenReportModal: boolean;
  isSubmitting: boolean;
  isSubmitted: boolean;
  onCloseReportModal: () => void;
  questionTitle: string;
  createReportParam: CreateReportRequest;
  onParamChange: (key: 'reportReason' | 'details', value: string) => void;
  handleCreateReport: () => void;
}

/**
 * 신고 모달
 *
 * @author hjkim
 * @constructor
 */
const ReportModal = ({
  isOpenReportModal,
  isSubmitting,
  isSubmitted,
  onCloseReportModal,
  questionTitle,
  createReportParam,
  onParamChange,
  handleCreateReport,
}: ReportModalProps) => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleCreateReport();
  };
  return (
    <Modal isOpen={isOpenReportModal} onClose={onCloseReportModal}>
      <ModalHeader>
        <ModalTitle className="flex justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <FlagIcon className="w-4 h-4 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-fore-ground">신고하기</h3>
              <p className="text-sm text-muted-foreground">부적절한 콘텐츠를 신고해주세요.</p>
            </div>
          </div>
          <div>
            <Button variant="ghost" size="sm" onClick={onCloseReportModal} className="w-8 h-8">
              X
            </Button>
          </div>
        </ModalTitle>
      </ModalHeader>
      <ModalContent className="p-6">
        {isSubmitted ? (
          <div className="text-center py-8">
            <CheckIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-fore-ground mb-2">신고가 접수되었습니다.</h3>
            <p className="text-muted-foreground mb-6">소중한 의견 감사합니다.</p>
            <Button onClick={onCloseReportModal} className="w-full">
              닫기
            </Button>
          </div>
        ) : (
          <form className="space-y-6" onSubmit={handleSubmit}>
            {questionTitle && (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <p className="text-sm text-muted-foreground mb-1">신고 대상</p>
                <p className="text-sm font-medium text-fore-ground line-clamp-2">{questionTitle}</p>
              </div>
            )}
            {/* 신고 사유 선택 */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-fore-ground">
                신고 사유<span className="text-red-600">*</span>
              </label>
              <div className="space-y-2">
                {REPORT_REASONS.map((reason) => (
                  <label
                    key={reason.id}
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                      createReportParam.reportReason === reason.id ? 'bg-primary/10 border-primary' : ''
                    }`}>
                    <input
                      type="radio"
                      name="reason"
                      value={reason.id}
                      checked={createReportParam.reportReason === reason.id}
                      onChange={() => onParamChange('reportReason', reason.id)}
                      className="mt-1 text-primary focus:ring-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-fore-ground">{reason.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">{reason.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            {createReportParam.reportReason === 'OTHER' && (
              <div className="space-y-2 animate-fade-in">
                <label htmlFor="details" className="text-sm font-medium text-fore-ground">
                  상세 사유<span className="text-red-600">*</span>
                </label>
                <textarea
                  id="details"
                  value={createReportParam.details}
                  onChange={(e) => onParamChange('details', e.target.value)}
                  className="w-full p-2 border rounded-md min-h-[100px] focus:ring-primary focus:border-primary"
                  placeholder="신고하시는 이유를 구체적으로 작성해주세요."
                />
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onCloseReportModal} className="flex-1">
                취소
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? '제출 중...' : '신고 제출'}
              </Button>
            </div>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ReportModal;
